import type { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip"
import { useCallback, useRef, useState, type ReactElement, type ReactNode } from "react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { clickHostOf, type ClickHost } from "./click-host.ts"
import { useViewport } from "./viewport.tsx"

/*
 * A fact that sits behind something already on the page and is worth reading: the exact moment
 * behind "5m ago", what a figure means behind its info icon, the names behind "+2". On the desk it
 * is the tooltip it always was: the same surface, the same 400ms wait, opening at once after
 * another tooltip, and on keyboard focus where its trigger is a `<button>`. It also opens on a click
 * or a tap, which is the only way touch reaches it, since touch shows no tooltips; opened that way
 * it stays until a press elsewhere, Esc, or a second click. A plain Tooltip only labels what is
 * already visible or named by its `aria-label`; anything a Member needs to read goes in a
 * Toggletip.
 *
 * A trigger inside something that acts on a click leaves the click to it on the desk, as a tooltip
 * did: an avatar stack in a ticket's row opens the ticket, and the names show on a hover. On touch
 * a tap there opens the Toggletip instead, since a tap that left the page would lose the fact.
 * Inside a button (an assignee picker's) the button keeps the tap too: what it opens shows the
 * same. So does a list's option or a menu's item, which the tap chooses. A DataTable row leaves a
 * tooltip's trigger its own click at every pointer.
 */

/**
 * `children` is the element it opens from, as the page already shows it; `tip` is what it says,
 * one line, or several stacked by the caller (a list of names).
 */
export function Toggletip({ tip, children }: { tip: ReactNode; children: ReactElement }) {
  const { touch } = useViewport()
  const [open, setOpen] = useState(false)
  const [host, setHost] = useState<ClickHost>("none")
  // Opened by a click or a tap, so the pointer leaving it doesn't close it.
  const pinned = useRef(false)
  const measure = useCallback((trigger: HTMLElement | null) => {
    if (trigger) setHost(clickHostOf(trigger))
  }, [])
  const yields = host === "control" || (host === "row" && !touch)
  return (
    <Tooltip
      open={open}
      onOpenChange={(next, { reason }: TooltipPrimitive.Root.ChangeEventDetails) => {
        if (!next && pinned.current && reason === "trigger-hover") return
        if (!next) pinned.current = false
        setOpen(next)
      }}
    >
      <TooltipTrigger
        ref={measure}
        render={children}
        // Where the click is its host's, it closes the tip as a tooltip's trigger does.
        closeOnClick={yields}
        onClick={(event) => {
          if (yields) return
          if (host === "row") {
            event.preventDefault()
            event.stopPropagation()
          }
          pinned.current = !(open && pinned.current)
          setOpen(pinned.current)
        }}
      />
      <TooltipContent>{tip}</TooltipContent>
    </Tooltip>
  )
}
